import React from 'react';
import { useNavigate } from 'react-router-dom';
import { GameData } from '../types';
import { Calendar, Tag, Star } from 'lucide-react';
import { cn } from '../utils/cn';

interface GameCardProps {
  game: GameData;
  className?: string;
}

const GameCard: React.FC<GameCardProps> = ({ game, className }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    // Extraire le path du jeu depuis l'URL
    const gamePath = game.url.replace('https://game3rb.com/', '').replace(/\/$/, '');
    navigate(`/game/${gamePath}`);
  };

  return (
    <div
      className={cn(
        'bg-white dark:bg-dark-800 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer group overflow-hidden',
        className
      )}
      onClick={handleClick}
    >
      <div className="relative">
        <img
          src={game.image_url}
          alt={game.title}
          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = '/placeholder-game.jpg';
          }}
        />
        {game.is_sticky && (
          <div className="absolute top-2 left-2 bg-primary-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
            <Star className="w-3 h-3 inline mr-1" />
            Épinglé
          </div>
        )}
        {game.badges.length > 0 && (
          <div className="absolute top-2 right-2 flex flex-col gap-1">
            {game.badges.slice(0, 2).map((badge, index) => (
              <span
                key={index}
                className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-semibold"
              >
                {badge}
              </span>
            ))}
          </div>
        )}
      </div>
      
      <div className="p-4">
        <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-2 line-clamp-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
          {game.title}
        </h3>
        
        {game.release_date && (
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-2">
            <Calendar className="w-4 h-4 mr-1" />
            {game.release_date}
          </div>
        )}
        
        {game.categories.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {game.categories.slice(0, 3).map((category, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200"
              >
                <Tag className="w-3 h-3 mr-1" />
                {category}
              </span>
            ))}
            {game.categories.length > 3 && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                +{game.categories.length - 3} more
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GameCard;
